import { useEffect, useMemo, useRef, useState } from "react";
import { Network, BellOff, Bell, Camera, Check, Copy, Crown, History, Link2, LogOut, RefreshCw, Search, Shield, ShieldOff, UserMinus, UserPlus, Users, X, Lock, BellRing } from "lucide-react";
import type { Message, Room, User } from "./types";
import { Avatar, Empty, Field, Modal, Spinner, useChat } from "./ui";
import { call, uploadFile } from "./api";
import { fmtDateTime, fmtDate, lastSeenText } from "./jalali";
import { fileUrl } from "./chatConfig";
import { enablePush, pushActive, pushSupported } from "./push";

/* ───────────────────────── گفتگوی جدید ───────────────────────── */
export function NewChatDialog({ onClose }: { onClose: () => void }) {
  const { me, users, statusOf, openDirect, openRoom, reloadRooms, toast } = useChat();
  const [mode, setMode] = useState<"direct" | "group">("direct");
  const [q, setQ] = useState(""); const [sel, setSel] = useState<number[]>([]);
  const [name, setName] = useState(""); const [desc, setDesc] = useState(""); const [busy, setBusy] = useState(false);
  const list = useMemo(() => users.filter(u => u.id !== me.id && (u.name || "").includes(q)), [users, q, me.id]);
  const toggle = (id: number) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const create = async () => {
    if (!name.trim()) return toast("نام گروه را وارد کنید", false);
    setBusy(true);
    const r = await call("room_create", { type: "group", name, description: desc, members: sel });
    setBusy(false);
    if (!r.success) return toast(r.message || "خطا در ساخت گروه", false);
    await reloadRooms(); onClose(); openRoom(r.room_id);
  };
  return (
    <Modal title="گفتگوی جدید" onClose={onClose} footer={mode === "group" ? <button className="cx-btn primary block" disabled={busy} onClick={create}>{busy ? <Spinner /> : <Users size={16} />} ساخت گروه ({sel.length} عضو)</button> : undefined}>
      <div className="cx-seg">
        <button className={mode === "direct" ? "on" : ""} onClick={() => setMode("direct")}>گفتگوی شخصی</button>
        <button className={mode === "group" ? "on" : ""} onClick={() => setMode("group")}>گروه جدید</button>
      </div>
      {mode === "group" && <>
        <Field label="نام گروه"><input className="cx-input" value={name} onChange={e => setName(e.target.value)} placeholder="مثلاً: تیم مالی" maxLength={120} autoFocus /></Field>
        <Field label="توضیحات (اختیاری)"><input className="cx-input" value={desc} onChange={e => setDesc(e.target.value)} maxLength={400} /></Field>
      </>}
      <div className="cx-search"><Search size={15} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجوی همکار…" /></div>
      <div className="cx-userlist">
        {list.map(u => (
          <button key={u.id} className={`cx-userrow ${sel.includes(u.id) ? "sel" : ""}`} onClick={() => mode === "direct" ? (openDirect(u.id), onClose()) : toggle(u.id)}>
            <Avatar name={u.name} src={u.avatar} size={38} status={statusOf(u.id)} />
            <div><b>{u.name}</b><small>{statusOf(u.id) === "online" ? "آنلاین" : statusOf(u.id) === "idle" ? "در سیستم (غیرفعال)" : "آخرین بازدید: " + lastSeenText(u.last_seen)}</small></div>
            {mode === "group" && <span className="cx-check">{sel.includes(u.id) && <Check size={14} />}</span>}
          </button>
        ))}
        {!list.length && <Empty title="کاربری پیدا نشد" />}
      </div>
    </Modal>
  );
}

/* ───────────────────────── اطلاعات گروه / گفتگو ───────────────────────── */
export function RoomInfoDialog({ room, onClose }: { room: Room; onClose: () => void }) {
  const { me, users, statusOf, reloadRooms, toast, ask, openDirect, openRoom } = useChat();
  const isGroup = room.type === "group";
  const owner = room.my_role === "owner"; const admin = owner || room.my_role === "admin";
  const [name, setName] = useState(room.name || ""); const [desc, setDesc] = useState(room.description || "");
  const [addSel, setAddSel] = useState<number[]>([]); const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const link = room.invite_code ? `${window.location.origin}${window.location.pathname}?join=${room.invite_code}` : "";
  const candidates = users.filter(u => !room.members.some(m => m.id === u.id));
  const run = async (action: string, body: any, ok?: string) => { setBusy(true); const r = await call(action, { room_id: room.id, ...body }); setBusy(false); if (!r.success) { toast(r.message || "خطا", false); return r; } await reloadRooms(); if (ok) toast(ok); return r; };

  const saveInfo = () => run("room_update", { name, description: desc }, "ذخیره شد");
  const pickAvatar = async (f?: File) => { if (!f) return; const u = await uploadFile(f, f.name); if (u.success) run("room_update", { avatar: u.url }, "تصویر گروه تغییر کرد"); else toast(u.message || "آپلود ناموفق", false); };
  const remove = async (id: number, nm: string) => { if (await ask({ title: `حذف ${nm} از گروه؟`, text: "پیام‌های قبلی او در گروه باقی می‌ماند و برای همه ثبت می‌شود.", confirm: "حذف عضو", danger: true })) run("room_remove_member", { user_id: id }, "عضو حذف شد"); };
  const leave = async () => { if (await ask({ title: "خروج از گروه؟", text: "دیگر پیام‌های این گروه را نخواهید دید.", confirm: "خروج", danger: true })) { await run("room_remove_member", { user_id: me.id }); onClose(); } };

  return (
    <Modal title={isGroup ? "اطلاعات گروه" : "اطلاعات گفتگو"} onClose={onClose} width={520}>
      <div className="cx-info-head">
        <div className="cx-info-avatar">
          <Avatar name={room.name} src={room.avatar} size={84} group={isGroup} />
          {isGroup && admin && <><button onClick={() => fileRef.current?.click()} aria-label="تغییر تصویر"><Camera size={15} /></button><input ref={fileRef} type="file" accept="image/*" hidden onChange={e => pickAvatar(e.target.files?.[0])} /></>}
        </div>
        {isGroup && admin ? (
          <div className="cx-info-edit">
            <input className="cx-input" value={name} onChange={e => setName(e.target.value)} maxLength={120} />
            <input className="cx-input" value={desc} onChange={e => setDesc(e.target.value)} placeholder="توضیحات گروه" maxLength={400} />
            <button className="cx-btn primary sm" disabled={busy || (name === room.name && desc === (room.description || ""))} onClick={saveInfo}>ذخیره</button>
          </div>
        ) : <div><h4>{room.name}</h4>{room.description && <p className="cx-muted">{room.description}</p>}</div>}
      </div>

      <div className="cx-info-actions">
        <button className="cx-btn" onClick={() => run("room_prefs", { muted: room.muted ? 0 : 1 }, room.muted ? "اعلان‌ها روشن شد" : "گفتگو بی‌صدا شد")}>{room.muted ? <Bell size={16} /> : <BellOff size={16} />} {room.muted ? "روشن‌کردن اعلان" : "بی‌صدا"}</button>
        <button className="cx-btn" onClick={() => run("room_prefs", { pinned: room.pinned ? 0 : 1 }, room.pinned ? "از بالای لیست برداشته شد" : "به بالای لیست سنجاق شد")}>{room.pinned ? "برداشتن سنجاق" : "سنجاق به بالای لیست"}</button>
      </div>

      {isGroup && admin && (
        <div className="cx-block">
          <label className="cx-switch"><input type="checkbox" checked={!!room.only_admins_post} onChange={e => run("room_update", { only_admins_post: e.target.checked ? 1 : 0 })} /><i /><span><b>فقط ادمین‌ها پیام بدهند</b><small>اعضا فقط می‌توانند بخوانند</small></span></label>
          <div className="cx-invite"><Link2 size={16} /><input readOnly value={link} onFocus={e => e.target.select()} />
            <button className="cx-icon-btn" onClick={() => { navigator.clipboard?.writeText(link); toast("لینک دعوت کپی شد"); }} aria-label="کپی"><Copy size={16} /></button>
            <button className="cx-icon-btn" onClick={async () => { if (await ask({ title: "ساخت لینک جدید؟", text: "لینک قبلی از کار می‌افتد.", confirm: "بله، بساز" })) run("room_invite", { reset: 1 }, "لینک جدید ساخته شد"); }} aria-label="لینک جدید"><RefreshCw size={16} /></button></div>
        </div>
      )}

      <div className="cx-block">
        <h5>اعضا ({room.members.length})</h5>
        {room.members.map(m => (
          <div key={m.id} className="cx-member">
            <Avatar name={m.name} src={m.avatar} size={38} status={statusOf(m.id)} />
            <div className="cx-member-info"><b>{m.name}{m.id === me.id ? " (شما)" : ""}</b><small>{statusOf(m.id) === "online" ? "آنلاین" : statusOf(m.id) === "idle" ? "در سیستم (غیرفعال)" : lastSeenText(m.last_seen)}</small></div>
            {m.role !== "member" && <span className={`cx-role ${m.role}`}>{m.role === "owner" ? <><Crown size={11} /> مالک</> : <><Shield size={11} /> ادمین</>}</span>}
            <div className="cx-member-act">
              {m.id !== me.id && <button className="cx-icon-btn" title="پیام خصوصی" onClick={() => { onClose(); openDirect(m.id); }}><Users size={15} /></button>}
              {isGroup && owner && m.role !== "owner" && <button className="cx-icon-btn" title={m.role === "admin" ? "برداشتن ادمینی" : "ادمین‌کردن"} onClick={() => run("room_set_role", { user_id: m.id, role: m.role === "admin" ? "member" : "admin" })}>{m.role === "admin" ? <ShieldOff size={15} /> : <Shield size={15} />}</button>}
              {isGroup && admin && m.id !== me.id && m.role !== "owner" && (owner || m.role === "member") && <button className="cx-icon-btn danger" title="حذف از گروه" onClick={() => remove(m.id, m.name)}><UserMinus size={15} /></button>}
            </div>
          </div>
        ))}
      </div>

      {isGroup && admin && candidates.length > 0 && (
        <div className="cx-block">
          <h5>افزودن عضو</h5>
          <div className="cx-chips">{candidates.map(u => <button key={u.id} className={addSel.includes(u.id) ? "on" : ""} onClick={() => setAddSel(s => s.includes(u.id) ? s.filter(x => x !== u.id) : [...s, u.id])}><Avatar name={u.name} src={u.avatar} size={22} /> {u.name}</button>)}</div>
          {addSel.length > 0 && <button className="cx-btn primary block" disabled={busy} onClick={async () => { const r = await run("room_add_members", { user_ids: addSel }, "اعضا اضافه شدند"); if (r.success) setAddSel([]); }}><UserPlus size={16} /> افزودن {addSel.length} نفر</button>}
        </div>
      )}
      {isGroup && <button className="cx-btn danger block" onClick={leave}><LogOut size={16} /> خروج از گروه</button>}
      <p className="cx-note"><Lock size={12} /> هیچ پیامی از دیتابیس حذف نمی‌شود؛ حذف و ویرایش‌ها برای همه اعضا قابل مشاهده است.</p>
    </Modal>
  );
}

/* ───────────────────────── تاریخچه ویرایش/حذف ───────────────────────── */
function diff(a: string, b: string) {
  let s = 0; while (s < a.length && s < b.length && a[s] === b[s]) s++;
  let ea = a.length, eb = b.length; while (ea > s && eb > s && a[ea - 1] === b[eb - 1]) { ea--; eb--; }
  return { pre: a.slice(0, s), oldMid: a.slice(s, ea), newMid: b.slice(s, eb), post: a.slice(ea) };
}
export function HistoryDialog({ messageId, onClose }: { messageId: number; onClose: () => void }) {
  const [data, setData] = useState<{ history: any[]; message: Message } | null>(null);
  useEffect(() => { call("message_audit", { message_id: messageId }).then(r => r.success && setData({ history: r.history, message: r.message })); }, [messageId]);
  const first = data?.history.find(h => h.action === "edit");
  const original = first ? first.old_content : data?.message.content;
  return (
    <Modal title={<><History size={18} /> تاریخچه پیام</>} onClose={onClose}>
      {!data ? <div className="cx-center"><Spinner size={26} /></div> : (
        <ol className="cx-timeline">
          <li><div className="cx-tl-dot" /><div className="cx-tl-body"><div className="cx-tl-head"><b>{data.message.sender_name}</b><span>نسخه اصلی • {fmtDateTime(data.message.created_at)}</span></div><div className="cx-tl-text">{original}</div></div></li>
          {data.history.map(h => {
            const d = h.action === "edit" ? diff(h.old_content || "", h.new_content || "") : null;
            return (
              <li key={h.id} className={h.action}><div className="cx-tl-dot" />
                <div className="cx-tl-body">
                  <div className="cx-tl-head"><b>{h.actor_name}</b><span>{h.action === "edit" ? "ویرایش کرد" : "پیام را حذف کرد"} • {fmtDateTime(h.created_at)}</span></div>
                  {d ? <>
                    <div className="cx-tl-from"><small>از</small><div>{d.pre}<del>{d.oldMid}</del>{d.post}</div></div>
                    <div className="cx-tl-to"><small>به</small><div>{d.pre}<ins>{d.newMid}</ins>{d.post}</div></div>
                  </> : <div className="cx-tl-text deleted">{h.old_content}</div>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Modal>
  );
}

/* ───────────────────────── خوانده‌شدن ───────────────────────── */
export function ReadersDialog({ message, onClose }: { message: Message; onClose: () => void }) {
  const [d, setD] = useState<{ readers: User[]; pending: User[] } | null>(null);
  useEffect(() => { call("message_readers", { message_id: message.id }).then(r => r.success && setD({ readers: r.readers, pending: r.pending })); }, [message.id]);
  return (
    <Modal title="اطلاعات پیام" onClose={onClose} width={400}>
      <div className="cx-info-msg">{message.type === "text" ? message.content : message.file_name || "پیام"}<small>ارسال: {fmtDateTime(message.created_at)}</small></div>
      {!d ? <div className="cx-center"><Spinner size={24} /></div> : <>
        <h5 className="cx-sec">دیده‌شده ({d.readers.length})</h5>
        {d.readers.map(u => <div key={u.id} className="cx-member"><Avatar name={u.name} src={u.avatar} size={32} /><div className="cx-member-info"><b>{u.name}</b></div><Check size={15} color="var(--ok)" /></div>)}
        {!d.readers.length && <p className="cx-muted">هنوز کسی ندیده است</p>}
        {d.pending.length > 0 && <><h5 className="cx-sec">هنوز ندیده‌اند ({d.pending.length})</h5>
          {d.pending.map(u => <div key={u.id} className="cx-member"><Avatar name={u.name} src={u.avatar} size={32} /><div className="cx-member-info"><b>{u.name}</b></div></div>)}</>}
      </>}
    </Modal>
  );
}

/* ───────────────────────── فوروارد ───────────────────────── */
export function ForwardDialog({ message, onClose }: { message: Message; onClose: () => void }) {
  const { rooms, toast } = useChat();
  const [sel, setSel] = useState<number[]>([]); const [q, setQ] = useState(""); const [busy, setBusy] = useState(false);
  const list = rooms.filter(r => (r.name || "").includes(q) && !(r.type === "group" && r.only_admins_post && r.my_role === "member"));
  return (
    <Modal title="فوروارد پیام" onClose={onClose} footer={<button className="cx-btn primary block" disabled={!sel.length || busy} onClick={async () => { setBusy(true); const r = await call("message_forward", { message_id: message.id, room_ids: sel }); setBusy(false); if (r.success) { toast(`به ${r.sent} گفتگو ارسال شد`); onClose(); } else toast(r.message || "خطا", false); }}>ارسال به {sel.length} گفتگو</button>}>
      <div className="cx-search"><Search size={15} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجوی گفتگو…" autoFocus /></div>
      <div className="cx-userlist">{list.map(r => (
        <button key={r.id} className={`cx-userrow ${sel.includes(r.id) ? "sel" : ""}`} onClick={() => setSel(s => s.includes(r.id) ? s.filter(x => x !== r.id) : [...s, r.id])}>
          <Avatar name={r.name} src={r.avatar} size={36} group={r.type === "group"} /><div><b>{r.name}</b><small>{r.type === "group" ? `${r.members.length} عضو` : "شخصی"}</small></div>
          <span className="cx-check">{sel.includes(r.id) && <Check size={14} />}</span>
        </button>))}</div>
    </Modal>
  );
}

/* ───────────────────────── پروفایل ───────────────────────── */
export function ProfileDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { me, toast } = useChat();
  const [name, setName] = useState(me.name); const [avatar, setAvatar] = useState(me.avatar || ""); const [up, setUp] = useState(false);
  const [push, setPush] = useState<boolean | null>(null); const [showMgr, setShowMgr] = useState(false); const ref = useRef<HTMLInputElement>(null);
  const mgr = (me as any).manager_name as string | undefined;
  useEffect(() => { pushActive().then(setPush); }, []);
  return (
    <Modal title="پروفایل من" onClose={onClose} width={420} footer={<button className="cx-btn primary block" onClick={async () => { const r = await call("profile_update", { full_name: name, avatar }); if (r.success) { toast("پروفایل ذخیره شد"); onSaved(); onClose(); } else toast(r.message || "خطا", false); }}>ذخیره تغییرات</button>}>
      <div className="cx-profile-av">
        <Avatar name={name} src={avatar} size={96} />
        <button onClick={() => ref.current?.click()} aria-label="تغییر عکس">{up ? <Spinner size={14} /> : <Camera size={16} />}</button>
        <input ref={ref} type="file" accept="image/*" hidden onChange={async e => { const f = e.target.files?.[0]; if (!f) return; setUp(true); const r = await uploadFile(f, f.name); setUp(false); if (r.success) setAvatar(r.url!); else toast(r.message || "آپلود ناموفق", false); }} />
      </div>
      <Field label="نام کامل"><input className="cx-input" value={name} onChange={e => setName(e.target.value)} maxLength={100} /></Field>
      <div className="cx-kv"><span>مافوق مستقیم</span><b>{mgr || "تعیین نشده"}</b></div>
      {(me as any).role === "admin" && <button className="cx-btn block" style={{ marginTop: 10 }} onClick={() => setShowMgr(true)}><Network size={16} /> تعیین مافوق همکاران (مدیر سیستم)</button>}
      {showMgr && <ManagersDialog onClose={() => setShowMgr(false)} />}
      {pushSupported() && (
        <div className="cx-kv"><span>اعلان روی این دستگاه</span>
          {push ? <b style={{ color: "var(--ok)" }}>فعال ✓</b> : <button className="cx-btn sm" onClick={async () => { const r = await enablePush(); if (r.ok) { setPush(true); toast("اعلان‌ها فعال شد"); } else toast(r.message || "ناموفق", false); }}><BellRing size={14} /> فعال‌سازی</button>}
        </div>
      )}
    </Modal>
  );
}

/* ───────────────────────── نمایش تصویر ───────────────────────── */
export function Lightbox({ msg, onClose }: { msg: Message; onClose: () => void }) {
  useEffect(() => { const h = (e: KeyboardEvent) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  return (
    <div className="cx-lightbox" onClick={onClose}>
      <div className="cx-lightbox-bar" onClick={e => e.stopPropagation()}><b>{msg.sender_name}</b><span>{fmtDate(msg.created_at)}</span><a className="cx-btn sm" href={fileUrl(msg.file_url)} download={msg.file_name || true} target="_blank" rel="noreferrer noopener">دانلود</a><button className="cx-icon-btn" onClick={onClose}><X size={20} /></button></div>
      <img src={fileUrl(msg.file_url)} alt={msg.file_name || ""} onClick={e => e.stopPropagation()} />
    </div>
  );
}

/* ───────────────────────── تعیین مافوق (فقط مدیر سیستم) ───────────────────────── */
export function ManagersDialog({ onClose }: { onClose: () => void }) {
  const { users, toast } = useChat();
  const [local, setLocal] = useState<Record<number, number>>(() => Object.fromEntries(users.map(u => [u.id, u.manager_id || 0] as [number, number])));
  const change = async (userId: number, managerId: number) => {
    const r = await call("set_manager", { user_id: userId, manager_id: managerId });
    if (!r.success) return toast(r.message || "ذخیره نشد", false);
    setLocal(p => ({ ...p, [userId]: managerId })); toast("مافوق ذخیره شد"); window.dispatchEvent(new Event("cx:users-changed"));
  };
  return (
    <Modal title={<><Network size={18} /> مافوق همکاران</>} onClose={onClose} width={520}>
      <p className="cx-muted" style={{ marginBottom: 12 }}>برای هر نفر مشخص کنید مافوقش کیست. مافوق، گزارش پیشرفت و درخواست تمدید وظایف او را می‌بیند و در تأخیر طولانی به او اطلاع داده می‌شود.</p>
      {users.map(u => (
        <div key={u.id} className="cx-member">
          <Avatar name={u.name} src={u.avatar} size={36} />
          <div className="cx-member-info"><b>{u.name}</b></div>
          <select className="cx-input sm" value={local[u.id] || 0} onChange={e => change(u.id, +e.target.value)}>
            <option value={0}>بدون مافوق</option>
            {users.filter(x => x.id !== u.id).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
      ))}
    </Modal>
  );
}
